import React, { useState } from 'react';
import { STRRoom } from './types';
import { formatNumber } from './utils';
import { useResponsive } from '../hooks/useResponsive';

interface Props {
  rooms: STRRoom[];
  loading?: boolean;
  currentRoom: STRRoom | null;
  onSelect: (room: STRRoom) => void;
  onEdit: (room: STRRoom) => void;
  onDelete: (room: STRRoom) => void;
  onCoManage: (room: STRRoom) => void;
  onAddRoom?: () => void;
  onShowDemo?: () => void;
}

const OnboardingIllustration = () => (
  <svg width="140" height="116" viewBox="0 0 140 116" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="60" cy="62" r="52" fill="#eff6ff" />
    <rect x="26" y="58" width="68" height="50" rx="3" fill="#dbeafe"/>
    <polygon points="12,60 60,16 108,60" fill="#1976d2" opacity="0.9"/>
    <rect x="46" y="76" width="28" height="32" rx="3" fill="#93c5fd"/>
    <circle cx="69" cy="93" r="2.5" fill="#1976d2"/>
    <rect x="30" y="68" width="16" height="14" rx="2" fill="#fff" opacity="0.85"/>
    <line x1="38" y1="68" x2="38" y2="82" stroke="#bfdbfe" strokeWidth="1"/>
    <line x1="30" y1="75" x2="46" y2="75" stroke="#bfdbfe" strokeWidth="1"/>
    <rect x="74" y="68" width="16" height="14" rx="2" fill="#fff" opacity="0.85"/>
    <line x1="82" y1="68" x2="82" y2="82" stroke="#bfdbfe" strokeWidth="1"/>
    <line x1="74" y1="75" x2="90" y2="75" stroke="#bfdbfe" strokeWidth="1"/>
    <circle cx="110" cy="26" r="12" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2"/>
    <circle cx="110" cy="26" r="5" fill="#fff"/>
    <rect x="119" y="23" width="20" height="5" rx="2.5" fill="#fbbf24"/>
    <rect x="134" y="28" width="5" height="8" rx="1.5" fill="#fbbf24"/>
    <rect x="126" y="28" width="5" height="6" rx="1.5" fill="#fbbf24"/>
    <rect x="4" y="24" width="28" height="26" rx="4" fill="#e0f2fe" stroke="#7dd3fc" strokeWidth="1.5"/>
    <rect x="4" y="30" width="28" height="4" fill="#38bdf8"/>
    <rect x="11" y="20" width="3" height="8" rx="1.5" fill="#94a3b8"/>
    <rect x="22" y="20" width="3" height="8" rx="1.5" fill="#94a3b8"/>
    <circle cx="11" cy="39" r="2" fill="#0ea5e9"/>
    <circle cx="18" cy="39" r="2" fill="#0ea5e9"/>
    <circle cx="25" cy="39" r="2" fill="#bfdbfe"/>
    <circle cx="11" cy="46" r="2" fill="#bfdbfe"/>
    <circle cx="18" cy="46" r="2" fill="#0ea5e9"/>
    <circle cx="25" cy="46" r="2" fill="#0ea5e9"/>
  </svg>
);

export default function RoomList({ rooms, loading, currentRoom, onSelect, onEdit, onDelete, onCoManage, onAddRoom, onShowDemo }: Props) {
  const { isMobile } = useResponsive();
  const [setupPopup, setSetupPopup] = useState<string | null>(null);

  if (loading) return null;

  if (rooms.length === 0) {
    return (
      <div style={{
        padding: '52px 24px 48px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28,
      }}>
        <OnboardingIllustration />

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 21, fontWeight: 700, color: '#1e293b', marginBottom: 10, letterSpacing: '-0.3px' }}>
            첫 번째 매물을 등록해보세요
          </div>
          <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7 }}>
            단기임대 예약·정산을 한 곳에서 스마트하게 관리하세요
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 580, width: '100%' }}>
          {[
            { icon: '📅', title: '예약 관리', desc: '달력으로 한눈에\n예약 현황 파악' },
            { icon: '💰', title: '정산 자동화', desc: '주·월별 수익을\n자동으로 계산' },
            { icon: '👥', title: '공동 관리', desc: '파트너와 함께\n실시간 공유' },
          ].map((f) => (
            <div key={f.title} style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14,
              padding: '18px 22px', textAlign: 'center', flex: '1 1 160px', minWidth: 140,
            }}>
              <div style={{ fontSize: 30, marginBottom: 10, lineHeight: 1 }}>{f.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 5 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{f.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <button
            onClick={onAddRoom}
            style={{
              padding: '13px 36px', background: '#1976d2', color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 4px 16px rgba(25,118,210,0.35)',
              letterSpacing: '-0.2px',
            }}
          >
            + 첫 번째 방 등록하기
          </button>
          <button
            onClick={onShowDemo}
            style={{
              background: 'none', border: 'none', color: '#1976d2',
              fontSize: 13, cursor: 'pointer',
              textDecoration: 'underline', textDecorationColor: 'rgba(25,118,210,0.4)',
              padding: 0,
            }}
          >
            예시 매물로 먼저 둘러보기 →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 2 }}>
      {rooms.map((room, idx) => {
        const active = currentRoom?.id === room.id;
        const activeBookings = (room.bookings || []).filter((b) => b.status !== 'cancelled');
        const isSublease = room.ownership_type === '전대 동의';
        const setupTotal = (room.setup_brokerage || 0) + (room.setup_interior || 0) + (room.setup_cleaning || 0);
        const dateRange = room.contract_start && room.contract_end
          ? `${room.contract_start.substring(2).replace(/-/g, '.')}~${room.contract_end.substring(2).replace(/-/g, '.')}`
          : '';
        const weekly = (room.weekly_rent || 0) + (room.weekly_maintenance || 0);
        const isDemo = room.id === '__demo__';

        let ownerText = room.ownership_type || '';
        const subleaseInfo = isSublease && (room.sublease_deposit || room.sublease_rent)
          ? `${formatNumber(Math.round((room.sublease_deposit || 0) / 10000))}만/${formatNumber(Math.round((room.sublease_rent || 0) / 10000))}만`
          : '';

        return (
          <div
            key={room.id}
            onClick={() => { setSetupPopup(null); onSelect(room); }}
            style={isMobile ? {
              display: 'flex', flexDirection: 'column', gap: 4, position: 'relative',
              padding: '10px 12px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
              background: active ? '#e3f2fd' : isDemo ? '#fffbeb' : '#fff',
              border: active ? '1px solid #1976d2' : isDemo ? '1px solid #fcd34d' : '1px solid #e2e5e9',
            } : {
              display: 'grid',
              gridTemplateColumns: '24px 1fr 150px 140px 110px 40px 90px 90px 30px 190px',
              alignItems: 'center', gap: '0 6px', padding: '8px 10px',
              background: active ? '#e3f2fd' : isDemo ? '#fffbeb' : '#fff',
              border: active ? '1px solid #1976d2' : isDemo ? '1px solid #fcd34d' : '1px solid #e2e5e9',
              borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {/* 번호 (데스크톱만) */}
            {!isMobile && <div style={{ fontSize: 12, color: '#999', textAlign: 'center' }}>{idx + 1}</div>}

            {/* 모바일: 1행 - 이름 + 가격 */}
            {isMobile ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle', background: room.status === 'active' ? '#4caf50' : '#bdbdbd' }} />
                  {room.name}
                  {room.is_collaborator && room.owner_name && <span style={{ fontSize: 10, color: '#ff9800', marginLeft: 4, fontWeight: 400 }}>({room.owner_name})</span>}
                  {isDemo && <span style={{ fontSize: 10, background: '#fef3c7', color: '#d97706', border: '1px solid #fcd34d', borderRadius: 4, padding: '1px 5px', marginLeft: 6, fontWeight: 600 }}>예시</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1976d2', whiteSpace: 'nowrap', marginLeft: 8 }}>
                  {formatNumber(weekly)}원/주
                </div>
              </div>
            ) : (
              <>
                {/* 데스크톱: 이름 */}
                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle', background: room.status === 'active' ? '#4caf50' : '#bdbdbd' }} />
                  {room.name}
                  {room.is_collaborator && room.owner_name && <span style={{ fontSize: 10, color: '#ff9800', marginLeft: 6, fontWeight: 400 }}>({room.owner_name})</span>}
                  {isDemo && <span style={{ fontSize: 10, background: '#fef3c7', color: '#d97706', border: '1px solid #fcd34d', borderRadius: 4, padding: '1px 5px', marginLeft: 6, fontWeight: 600 }}>예시</span>}
                </div>
              </>
            )}

            {/* 모바일: 2행 - 소유형태 + 계약기간 */}
            {isMobile ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
                {ownerText && <span style={{ color: '#1976d2', fontWeight: 600 }}>{ownerText}</span>}
                {subleaseInfo && <span style={{ color: '#e65100' }}>{subleaseInfo}</span>}
                {dateRange && <span style={{ color: '#888' }}>{dateRange}</span>}
                {setupTotal > 0 && <span style={{ color: '#555' }}>세팅비 {formatNumber(setupTotal)}</span>}
              </div>
            ) : (
              <>
                {/* 데스크톱: 소유형태 */}
                <div style={{ fontSize: 11, color: '#1976d2', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {ownerText}
                  {subleaseInfo && <span style={{ fontWeight: 400, color: '#e65100', marginLeft: 4 }}>{subleaseInfo}</span>}
                </div>
                {/* 데스크톱: 계약기간 */}
                <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>{dateRange}</div>
              </>
            )}

            {/* 모바일: 3행 - 유형+면적+사업자+예약건수 */}
            {isMobile ? (
              <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#888' }}>
                <span>{room.room_type}{room.area_m2 ? ` ${room.area_m2}m²` : ''}</span>
                {room.business_type && <span>{room.business_type}</span>}
                <span>{activeBookings.length}건</span>
              </div>
            ) : (
              <>
                {/* 데스크톱: 세팅비 */}
                <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap', position: 'relative' }}>
                  {setupTotal > 0 ? (
                    <span onClick={(e) => { e.stopPropagation(); setSetupPopup(setupPopup === room.id ? null : room.id); }} style={{ cursor: 'pointer' }}>
                      <span style={{ color: '#999', fontWeight: 400 }}>세팅비</span> {formatNumber(setupTotal)}
                    </span>
                  ) : '-'}
                  {setupPopup === room.id && setupTotal > 0 && (
                    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 100, background: '#fff', border: '1px solid #e2e5e9', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 160, fontSize: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>세팅비 내역</div>
                      {room.setup_brokerage ? <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}><span style={{ color: '#888' }}>중개보수</span><span>{formatNumber(room.setup_brokerage)}원</span></div> : null}
                      {room.setup_interior ? <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}><span style={{ color: '#888' }}>내부인테리어</span><span>{formatNumber(room.setup_interior)}원</span></div> : null}
                      {room.setup_cleaning ? <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}><span style={{ color: '#888' }}>입주청소</span><span>{formatNumber(room.setup_cleaning)}원</span></div> : null}
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', paddingTop: 4, marginTop: 4, fontWeight: 600 }}><span>합계</span><span>{formatNumber(setupTotal)}원</span></div>
                    </div>
                  )}
                </div>
                {/* 데스크톱: 사업자유형 */}
                <div style={{ fontSize: 11, color: '#555', textAlign: 'center' }}>{room.business_type || ''}</div>
                {/* 데스크톱: 유형+면적 */}
                <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>{room.room_type}{room.area_m2 ? ` ${room.area_m2}m²` : ''}</div>
                {/* 데스크톱: 가격 */}
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1976d2', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(weekly)}원/주</div>
                {/* 데스크톱: 예약 건수 */}
                <div style={{ fontSize: 11, color: '#888', textAlign: 'center', whiteSpace: 'nowrap' }}>{activeBookings.length}건</div>
              </>
            )}

            {/* 버튼 */}
            <div style={{ display: 'flex', gap: isMobile ? 4 : 4, flexWrap: 'wrap', marginTop: isMobile ? 2 : 0 }} onClick={(e) => e.stopPropagation()}>
              {isDemo ? (
                <span style={{ fontSize: isMobile ? 10 : 11, color: '#d97706', background: '#fef3c7', padding: isMobile ? '2px 6px' : '4px 8px', borderRadius: isMobile ? 4 : 6, fontWeight: 600, border: '1px solid #fcd34d', lineHeight: isMobile ? 1.2 : 'normal' }}>예시 매물</span>
              ) : room.is_collaborator ? (
                <span style={{ fontSize: isMobile ? 10 : 11, color: '#fff', background: '#ff9800', padding: isMobile ? '2px 6px' : '4px 8px', borderRadius: isMobile ? 4 : 6, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', lineHeight: isMobile ? 1.2 : 'normal' }}>공동</span>
              ) : (room.collaborators && room.collaborators.length > 0) ? (
                <button onClick={() => onCoManage(room)} style={{ padding: isMobile ? '2px 6px' : '4px 10px', fontSize: isMobile ? 10 : 12, fontWeight: 600, border: '1px solid #1976d2', background: '#e3f2fd', borderRadius: isMobile ? 4 : 6, cursor: 'pointer', color: '#1976d2', whiteSpace: 'nowrap', lineHeight: isMobile ? 1.2 : 'normal' }}>
                  공동 {room.collaborators.length}명
                </button>
              ) : (
                <button onClick={() => onCoManage(room)} style={{ padding: isMobile ? '2px 6px' : '4px 10px', fontSize: isMobile ? 10 : 12, fontWeight: 500, border: '1px solid #e2e5e9', background: '#f5f5f5', borderRadius: isMobile ? 4 : 6, cursor: 'pointer', color: '#666', whiteSpace: 'nowrap', lineHeight: isMobile ? 1.2 : 'normal' }}>
                  단독관리
                </button>
              )}
              {!isDemo && (
                <button onClick={() => onEdit(room)} style={{ padding: isMobile ? '2px 6px' : '4px 10px', fontSize: isMobile ? 10 : 12, fontWeight: 500, border: '1px solid #e2e5e9', background: '#f5f5f5', borderRadius: isMobile ? 4 : 6, cursor: 'pointer', color: '#333', lineHeight: isMobile ? 1.2 : 'normal' }}>수정</button>
              )}
              {!room.is_collaborator && !isDemo && (
                <button onClick={() => onDelete(room)} style={{ padding: isMobile ? '2px 6px' : '4px 10px', fontSize: isMobile ? 10 : 12, fontWeight: 500, border: '1px solid #e53935', background: '#fff', borderRadius: isMobile ? 4 : 6, cursor: 'pointer', color: '#e53935', lineHeight: isMobile ? 1.2 : 'normal' }}>삭제</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
